import React, { useEffect, useMemo, useState } from "react";
import Aside from "../components/Aside";

import { DayPicker } from "react-day-picker";
import { ptBR } from "date-fns/locale";
import { format, parseISO, isBefore } from "date-fns";
import "react-day-picker/dist/style.css";

import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";

import { MdInfoOutline } from "react-icons/md";

import { database } from "../services/firebase";
import { ref, onValue } from "firebase/database";

const History = () => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const ASIDE_W = 260;
  const MOBILE_TOPBAR_H = 64;

  const [reservas, setReservas] = useState({});
  const [selectedDay, setSelectedDay] = useState(new Date);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedReserva, setSelectedReserva] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");

  const [dateDialogOpen, setDateDialogOpen] = useState(false);

  const showSnack = (msg) => {
    setSnackMsg(msg);
    setSnackOpen(true);
  };

  // =========================
  // CARREGA HISTÓRICO
  // =========================
  useEffect(() => {
    const refReservas = ref(database, "reservas");
    const unsub = onValue(refReservas, (snap) => {
      setReservas(snap.exists() ? snap.val() : {});
    });
    return () => unsub();
  }, []);

  // =========================
  // LISTA FILTRADA
  // =========================
  const reservasList = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const list = [];

    Object.entries(reservas).forEach(([dateKey, periodos]) => {
      const dateObj = parseISO(dateKey);

      // só histórico (datas passadas)
      if (!isBefore(dateObj, today)) return;

      // filtro por dia selecionado
      if (selectedDay) {
        if (
          format(dateObj, "yyyy-MM-dd") !==
          format(selectedDay, "yyyy-MM-dd")
        ) {
          return;
        }
      }

      Object.entries(periodos || {}).forEach(([periodo, reservasPeriodo]) => {
        Object.entries(reservasPeriodo || {}).forEach(([id, r]) => {
          list.push({
            id,
            date: dateKey,
            periodo,
            ...r,
          });
        });
      });
    });

    return list.sort((a, b) => {
      if (a.date !== b.date) return b.date.localeCompare(a.date);
      return (a.horario || "").localeCompare(b.horario || "");
    });
  }, [reservas, selectedDay]);

  const openDetails = (reserva) => {
    setSelectedReserva(reserva);
    setDetailsOpen(true);
  };

  const disableFutureDays = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // bloqueia datas futuras
    return day > today;
  };

  return (
    <>
      <Aside />

      <Box
        sx={{
          minHeight: "100vh",
          background: "var(--offwhite)",
          ml: isMobile ? 0 : `${ASIDE_W}px`,
          pt: isMobile ? `${MOBILE_TOPBAR_H + 16}px` : 3,
          pb: 4,
        }}
      >
        <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 3 } }}>
          <Stack spacing={2} mb={2}>
            <Typography variant="h5" sx={{ fontWeight: 1000, color: "var(--azulEscuro)", textAlign: "left" }}>
              Histórico de Reservas
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid var(--input-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                maxWidth: "100%",
              }}
            >
              <Stack spacing={0.5}>
                <Typography sx={{ fontWeight: 900, color: "var(--azulEscuro)" }}>
                  Data selecionada
                </Typography>

                <Typography sx={{ fontWeight: 700 }}>
                  {selectedDay
                    ? format(selectedDay, "dd/MM/yyyy")
                    : "Selecione uma data"}
                </Typography>
              </Stack>

              <Button
                variant="outlined"
                onClick={() => setDateDialogOpen(true)}
                sx={{
                  fontWeight: 900,
                  borderRadius: 2,
                  px: 3,
                  height: 42,
                }}
              >
                Escolher data
              </Button>
            </Paper>
          </Stack>

          <Paper elevation={0} sx={{ border: "1px solid var(--input-border)", borderRadius: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900 }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Período</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Pessoas</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>
                      Ação
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {reservasList.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {format(parseISO(r.date), "dd/MM/yyyy")}
                      </TableCell>

                      <TableCell>{r.primeiroNome}  {r.sobrenome}</TableCell>

                      <TableCell>
                        <Chip
                          label={r.periodo === "almoco" ? "Almoço" : "Jantar"}
                          size="small"
                          sx={{ fontWeight: 900 }}
                        />
                      </TableCell>

                      <TableCell>{r.pessoas}</TableCell>

                      <TableCell align="right">
                        <Tooltip title="Detalhes">
                          <IconButton onClick={() => openDetails(r)}>
                            <MdInfoOutline />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {reservasList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography sx={{ fontWeight: 700, color: "var(--cinza)" }}>
                          Nenhuma reserva encontrada no histórico.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Container>
      </Box>

      {/* DIALOG DETALHES */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, color: "var(--azulEscuro)" }}>
          Detalhes da Reserva
        </DialogTitle>

        <DialogContent dividers>
          {selectedReserva && (
            <Stack spacing={1.2}>
              <Typography><b>Data:</b> {format(parseISO(selectedReserva.date), "dd/MM/yyyy")}</Typography>
              <Typography><b>Nome:</b> {selectedReserva.primeiroNome + selectedReserva.sobrenome}</Typography>
              <Typography><b>Telefone:</b> {selectedReserva.telefone}</Typography>
              <Typography><b>Período:</b> {selectedReserva.periodo.toUpperCase()}</Typography>
              <Typography><b>Pessoas:</b> {selectedReserva.pessoas}</Typography>
              <Typography><b>Local:</b> {selectedReserva.local}</Typography>

              <Divider />

              <Typography fontWeight={800}>Mesas</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {(selectedReserva.mesas || []).map((m) => (
                  <Chip key={m} label={m} size="small" sx={{ fontWeight: 900 }} />
                ))}
              </Stack>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} sx={{ fontWeight: 900 }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity="success"
          sx={{ fontSize: 16, fontWeight: 700 }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>

      <Dialog
        open={dateDialogOpen}
        onClose={() => setDateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900, color: "var(--azulEscuro)" }}>
          Selecionar data
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <DayPicker
              mode="single"
              locale={ptBR}
              selected={selectedDay}
              onSelect={(day) => {
                setSelectedDay(day);
                setDateDialogOpen(false); // fecha ao selecionar
              }}
              disabled={disableFutureDays}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDateDialogOpen(false)}
            sx={{ fontWeight: 900 }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default History;
